import { StaffLayout } from "@/components/layouts/staff-layout"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { usePage } from "@inertiajs/react"
import { t } from "@/lib/i18n"
type QuickStat = {
    id: string
    label: string
    value: number
    description: string
}
type StatusCard = {
    value: string
    label: string
    count: number
}
type RecentOrder = {
    id: number
    name: string
    statusLabel: string
    serviceTypeLabel: string
    tableName: string | null
    customerName: string
    createdAt: string
}
type Props = {
    quickStats: QuickStat[]
    orderStatusCards: StatusCard[]
    tableStatusCards: StatusCard[]
    stationCards: StatusCard[]
    recentOrders: RecentOrder[]
}
function formatDateTime(value: string) {
    return new Intl.DateTimeFormat("es-MX", {
        dateStyle: "short",
        timeStyle: "short",
    }).format(new Date(value))
}
export default function StaffLocationManageDashboardShow() {
    const { quickStats, orderStatusCards, tableStatusCards, stationCards, recentOrders } = usePage<Props>().props
    return (<StaffLayout>
      <MainContainer size="md">
        <div className="space-y-6" data-testid="manage-dashboard-view">
          <PageHeader title={t("ui.react.pages.staff.location.manage.dashboard.show.dashboard_management")} description={t("ui.react.pages.staff.location.manage.dashboard.show.visibility_quick_operation_restaurant")}/>

          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {quickStats.map((stat) => (<Card key={stat.id} className="gap-2">
                <CardHeader className="gap-1">
                  <CardDescription>{stat.label}</CardDescription>
                  <CardTitle className="text-3xl">{stat.value}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">{stat.description}</p>
                </CardContent>
              </Card>))}
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t("ui.react.pages.staff.location.manage.dashboard.show.orders_status")}</CardTitle>
                <CardDescription>{t("ui.react.pages.staff.location.manage.dashboard.show.status_current_flow_operational")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {orderStatusCards.map((card) => (<div key={card.value} className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 shadow-xs">
                    <span className="text-sm">{card.label}</span>
                    <span className="text-sm font-semibold">{card.count}</span>
                  </div>))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t("ui.react.pages.staff.location.manage.dashboard.show.tables_status")}</CardTitle>
                <CardDescription>{t("ui.react.pages.staff.location.manage.dashboard.show.distribution_current_tables")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {tableStatusCards.map((card) => (<div key={card.value} className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 shadow-xs">
                    <span className="text-sm">{card.label}</span>
                    <span className="text-sm font-semibold">{card.count}</span>
                  </div>))}
              </CardContent>
            </Card>
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t("ui.react.pages.staff.location.manage.dashboard.show.load_station")}</CardTitle>
                <CardDescription>{t("ui.react.pages.staff.location.manage.dashboard.show.orders_active_assigned_station")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {stationCards.map((card) => (<div key={card.value} className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 shadow-xs">
                    <span className="text-sm">{card.label}</span>
                    <span className="text-sm font-semibold">{card.count}</span>
                  </div>))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t("ui.react.pages.staff.location.manage.dashboard.show.orders_recent")}</CardTitle>
                <CardDescription>{t("ui.react.pages.staff.location.manage.dashboard.show.latest_orders_active_registered")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {recentOrders.length === 0 ? (<p className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">{t("ui.react.pages.staff.location.manage.dashboard.show.no_recent_active_orders")}</p>) : (recentOrders.map((order) => (<div key={order.id} className="space-y-1 rounded-lg border bg-card p-3 shadow-xs">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium">
                          #{order.id} · {order.name}
                        </p>
                        <span className="text-xs text-muted-foreground">{order.statusLabel}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {order.serviceTypeLabel}
                        {order.tableName ? ` · ${t("ui.common.table", { count: 1 })} ${order.tableName}` : ` · ${t("ui.common.no_table")}`}
                        {" · "}{t("ui.react.pages.staff.location.manage.dashboard.show.customer")}{order.customerName}
                      </p>
                      <p className="text-xs text-muted-foreground">{t("ui.react.pages.staff.location.manage.dashboard.show.created")}{formatDateTime(order.createdAt)}
                      </p>
                    </div>)))}
              </CardContent>
            </Card>
          </section>
        </div>
      </MainContainer>
    </StaffLayout>)
}
